import { useEffect, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import SkeletonRow from '@/components/SkeletonRow';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

type DuelRow = {
  id: string;
  challenger_id: string | null;
  challenged_id: string | null;
  winner_user_id: string | null;
  loser_user_id: string | null;
  resolved_at: string | null;
  status?: string | null;
  challenger_score?: number | null;
  challenged_score?: number | null;
  end_at?: string | null;
};

type PublicProfileRow = {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  lifting_score?: number | null;
};

const shortId = (value?: string | null) => (value ? value.slice(0, 8) : '—');

const displayName = (p?: PublicProfileRow | null, fallbackId?: string | null) => {
  if (p?.handle) return `@${p.handle}`;
  if (p?.display_name) return p.display_name;
  return fallbackId ? shortId(fallbackId) : 'Unknown user';
};

export default function DuelDetailScreen() {
  const { theme } = useTheme();
  const router = useRouter();
  const { profile } = useAuth();
  const { duel_id } = useLocalSearchParams();
  const duelId = typeof duel_id === 'string' ? duel_id : '';

  const [duel, setDuel] = useState<DuelRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [profiles, setProfiles] = useState<Record<string, PublicProfileRow>>({});

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!duelId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setNotFound(false);

      try {
        // Try history view first (completed duels)
        let { data, error } = await supabase
          .from('public_duel_history_v1')
          .select('duel_id,challenge_user_id,opponent_user_id,winner_user_id,loser_user_id,resolved_at')
          .eq('duel_id', duelId)
          .maybeSingle();

        // Fall back to duels table for active/pending duels not yet in history view
        if (!data) {
          const { data: liveData } = await supabase
            .from('duels')
            .select('id,challenger_id,challenged_id,winner_user_id,loser_user_id,resolved_at,status,challenger_score,challenged_score,end_at')
            .eq('id', duelId)
            .maybeSingle();
          if (liveData) {
            data = {
              duel_id: liveData.id,
              challenge_user_id: liveData.challenger_id,
              opponent_user_id: liveData.challenged_id,
              winner_user_id: liveData.winner_user_id,
              loser_user_id: liveData.loser_user_id,
              resolved_at: liveData.resolved_at,
              status: liveData.status,
              challenger_score: liveData.challenger_score,
              challenged_score: liveData.challenged_score,
              end_at: liveData.end_at,
            } as any;
          }
        }

        if (!data) {
          if (active) setNotFound(true);
          return;
        }

        const duelRow: DuelRow = {
          id: (data as any).duel_id ?? duelId,
          challenger_id: (data as any).challenge_user_id ?? null,
          challenged_id: (data as any).opponent_user_id ?? null,
          winner_user_id: (data as any).winner_user_id ?? null,
          loser_user_id: (data as any).loser_user_id ?? null,
          resolved_at: (data as any).resolved_at ?? null,
          status: (data as any).status ?? null,
          challenger_score: (data as any).challenger_score ?? null,
          challenged_score: (data as any).challenged_score ?? null,
          end_at: (data as any).end_at ?? null,
        };

        if (active) setDuel(duelRow);

        const ids = [duelRow.challenger_id, duelRow.challenged_id].filter(Boolean) as string[];
        if (ids.length > 0) {
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id,handle,display_name,gym_id,avatar_url')
            .in('id', ids);
          const map: Record<string, PublicProfileRow> = {};
          (profilesData || []).forEach((p: any) => {
            map[p.id] = p as PublicProfileRow;
          });
          if (active) setProfiles(map);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [duelId]);

  const opponentId = useMemo(() => {
    if (!duel || !profile?.id) return null;
    if (profile.id === duel.challenger_id) return duel.challenged_id;
    if (profile.id === duel.challenged_id) return duel.challenger_id;
    return null;
  }, [duel, profile?.id]);
  const challengerProfile = duel?.challenger_id ? profiles[duel.challenger_id] : null;
  const challengedProfile = duel?.challenged_id ? profiles[duel.challenged_id] : null;

  // "Why this opponent" — strength band explanation
  const matchExplanation = useMemo(() => {
    if (!duel || !profile?.id) return null;
    const myProfile = profile.id === duel.challenger_id ? challengerProfile : challengedProfile;
    const theirProfile = profile.id === duel.challenger_id ? challengedProfile : challengerProfile;
    const myScore = (myProfile as any)?.lifting_score ?? (profile as any)?.lifting_score ?? 0;
    const theirScore = (theirProfile as any)?.lifting_score ?? 0;
    if (myScore <= 0 || theirScore <= 0) return null;
    const diff = Math.abs(myScore - theirScore);
    const pct = Math.round((diff / myScore) * 100);
    return `Within ${pct}% of your strength score`;
  }, [duel, profile?.id, challengerProfile, challengedProfile]);

  const hasResult = Boolean(duel?.resolved_at && duel?.winner_user_id);
  const isWinner = duel?.winner_user_id && duel.winner_user_id === profile?.id;
  const isLoser = duel?.loser_user_id && duel.loser_user_id === profile?.id;
  const winnerName = duel?.winner_user_id ? displayName(profiles[duel.winner_user_id], duel.winner_user_id) : '—';

  const onRematch = () => {
    if (!opponentId) return;
    router.push(`/(tabs)/duels?opponent_user_id=${opponentId}`);
  };

  const backHeader = (
    <View style={[styles.backHeader, { borderBottomColor: theme.colors.border }]}>
      <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.push('/(tabs)/duels')} hitSlop={12}>
        <ArrowLeft size={24} color={theme.colors.text} />
      </TouchableOpacity>
      <Text style={[styles.backHeaderTitle, { color: theme.colors.text }]}>Duel</Text>
      <View style={{ width: 24 }} />
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} edges={['top']}>
        <Stack.Screen options={{ headerShown: false }} />
        {backHeader}
        <SkeletonRow rows={3} />
      </SafeAreaView>
    );
  }

  if (notFound || !duel) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} edges={['top']}>
        <Stack.Screen options={{ headerShown: false }} />
        {backHeader}
        <Text style={[styles.mutedText, { color: theme.colors.textSecondary }]}>Duel not found</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} edges={['top']}>
      <Stack.Screen options={{ headerShown: false }} />
      {backHeader}
      <ScrollView contentContainerStyle={styles.content}>

        {/* Status badge */}
        {duel.status && !hasResult && (
          <View style={[styles.statusBadge, {
            backgroundColor: duel.status === 'active' ? theme.colors.accent + '20' : theme.colors.surfaceVariant,
            borderColor: duel.status === 'active' ? theme.colors.accent : theme.colors.border,
          }]}>
            <Text style={[styles.statusText, { color: duel.status === 'active' ? theme.colors.accent : theme.colors.textSecondary }]}>
              {duel.status === 'active' ? '⚔️ In Progress' : duel.status === 'pending' ? '⏳ Awaiting Acceptance' : duel.status}
            </Text>
            {duel.status === 'active' && duel.end_at && (() => {
              const ms = new Date(duel.end_at).getTime() - Date.now();
              if (ms <= 0) return null;
              const h = Math.floor(ms / 3600000);
              const m = Math.floor((ms % 3600000) / 60000);
              return <Text style={[styles.statusText, { color: theme.colors.textTertiary, marginLeft: 8 }]}>{h}h {m}m left</Text>;
            })()}
          </View>
        )}

        {/* Tug-of-war progress bar — only for active duels with scores */}
        {duel.status === 'active' && (duel.challenger_score != null || duel.challenged_score != null) && (() => {
          const myId = profile?.id;
          const iAmChallenger = myId === duel.challenger_id;
          const myScore = iAmChallenger ? (duel.challenger_score ?? 0) : (duel.challenged_score ?? 0);
          const theirScore = iAmChallenger ? (duel.challenged_score ?? 0) : (duel.challenger_score ?? 0);
          const total = myScore + theirScore;
          const myPct = total > 0 ? (myScore / total) * 100 : 50;
          const opponentName = displayName(iAmChallenger ? challengedProfile : challengerProfile, opponentId);
          return (
            <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
              <Text style={[styles.label, { color: theme.colors.textSecondary, marginBottom: 8 }]}>Progress</Text>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text style={[styles.value, { color: theme.colors.accent, fontSize: 14 }]}>You — {Math.round(myScore)}</Text>
                <Text style={[styles.value, { color: theme.colors.textSecondary, fontSize: 14 }]}>{opponentName} — {Math.round(theirScore)}</Text>
              </View>
              <View style={{ height: 12, borderRadius: 2, backgroundColor: theme.colors.surfaceVariant, overflow: 'hidden' }}>
                <View style={{
                  height: '100%',
                  width: `${Math.max(4, Math.min(96, myPct))}%`,
                  backgroundColor: theme.colors.accent,
                  borderRadius: 2,
                }} />
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                <Text style={{ fontSize: 10, color: theme.colors.textTertiary }}>← You winning</Text>
                <Text style={{ fontSize: 10, color: theme.colors.textTertiary }}>Opponent winning →</Text>
              </View>
            </View>
          );
        })()}

        <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Challenger</Text>
          <Text style={[styles.value, { color: theme.colors.text }]}>
            {displayName(challengerProfile, duel.challenger_id)}
          </Text>

          <Text style={[styles.label, { color: theme.colors.textSecondary, marginTop: 12 }]}>Challenged</Text>
          <Text style={[styles.value, { color: theme.colors.text }]}>
            {displayName(challengedProfile, duel.challenged_id)}
          </Text>
        </View>

        {matchExplanation && (
          <View style={[styles.matchReasonCard, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border }]}>
            <Text style={[styles.label, { color: theme.colors.textSecondary }]}>Why This Opponent</Text>
            <Text style={[styles.matchReasonText, { color: theme.colors.text }]}>
              {matchExplanation}
            </Text>
          </View>
        )}

        {hasResult && (
          <View
            style={[
              styles.noticeBanner,
              {
                backgroundColor: theme.colors.surfaceVariant,
                borderColor: theme.colors.border,
              },
            ]}>
            <Text style={[styles.noticeText, { color: theme.colors.textSecondary }]}>
              Result recorded. Your record and rank are updated.
            </Text>
          </View>
        )}

        {hasResult && (
          <View
            style={[
              styles.banner,
              {
                backgroundColor: isWinner
                  ? theme.colors.success
                  : isLoser
                  ? theme.colors.error
                  : theme.colors.surfaceVariant,
              },
            ]}>
            <Text style={[styles.bannerText, { color: theme.colors.text }]}>
              {isWinner ? 'You won' : isLoser ? 'You lost' : `Winner: ${winnerName}`}
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={[
            styles.rematchButton,
            {
              backgroundColor: opponentId ? theme.colors.accent : theme.colors.surfaceVariant,
            },
          ]}
          onPress={onRematch}
          disabled={!opponentId}>
          <Text style={[styles.rematchText, { color: theme.colors.accentForeground }]}>Rematch</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderRadius: 2,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  statusText: {
    fontSize: 13,
    fontWeight: '700',
  },
  backHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backHeaderTitle: {
    fontSize: 17,
    fontWeight: '700',
  },
  content: {
    padding: 20,
    gap: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
  },
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  value: {
    fontSize: 16,
    fontWeight: '600',
  },
  banner: {
    borderRadius: 2,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  bannerText: {
    fontSize: 14,
    fontWeight: '700',
  },
  noticeBanner: {
    borderRadius: 2,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  noticeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  rematchButton: {
    borderRadius: 2,
    paddingVertical: 12,
    alignItems: 'center',
  },
  rematchText: {
    fontSize: 15,
    fontWeight: '700',
  },
  mutedText: {
    fontSize: 14,
    fontWeight: '500',
  },
  matchReasonCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
  },
  matchReasonText: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 4,
  },
});
